# ParkRules.vue
<template>
  <div class="rules-section">
    <!-- Banner Section -->
    <Banner
      image="https://images.unsplash.com/photo-1562577309-2592ab84b1bc?q=80&w=2070"
      :title="$t('park_rules.title')"
      height="700px"
    />

    <!-- Wavy Divider -->
    <div class="wave-container">
      <svg class="wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 400" preserveAspectRatio="none">
        <path class="wave-path" d="M0,96L40,106.7C80,117,160,139,240,154.7C320,171,400,181,480,165.3C560,149,640,107,720,90.7C800,75,880,85,960,112C1040,139,1120,181,1200,181.3C1280,181,1360,139,1400,117.3L1440,96L1440,400L1400,400C1360,400,1280,400,1200,400C1120,400,1040,400,960,400C880,400,800,400,720,400C640,400,560,400,480,400C400,400,320,400,240,400C160,400,80,400,40,400L0,400Z"></path>
      </svg>
    </div>

    <div class="container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading park rules...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <i class="fas fa-exclamation-circle"></i>
        <p>{{ error }}</p>
        <button @click="fetchRules" class="retry-btn">
          <i class="fas fa-redo"></i> Retry
        </button>
      </div>

      <!-- Content -->
      <div v-else class="rules-content">
        <div class="content-wrapper" v-html="rulesContent"></div>

        <!-- Additional Guidelines Section -->
        <div class="guidelines-section">
          <h3>Important Guidelines</h3>
          <div class="guidelines-grid">
            <div class="guideline-card">
              <i class="fas fa-exclamation-triangle"></i>
              <h4>Safety First</h4>
              <p>Follow all posted safety instructions and lifeguard directions</p>
            </div>
            <div class="guideline-card">
              <i class="fas fa-swimming-pool"></i>
              <h4>Pool Rules</h4>
              <p>No diving in shallow areas. Children must be supervised</p>
            </div>
            <div class="guideline-card">
              <i class="fas fa-camera"></i>
              <h4>Photography</h4>
              <p>Photography is allowed in designated areas only</p>
            </div>
            <div class="guideline-card">
              <i class="fas fa-ban"></i>
              <h4>Prohibited Items</h4>
              <p>No outside food, drinks, or glass containers allowed</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import Banner from "@/components/Banner.vue";
import axiosInstance from '@/plugins/axios';
import { useSettingStore } from '@/stores/settingStore';

const settingStore = useSettingStore();
const rulesData = ref(null);
const loading = ref(true);
const error = ref(null);

// Compute content based on current language
const rulesContent = computed(() => {
  if (!rulesData.value) return '';
  return settingStore.language === 'en' 
    ? rulesData.value.value_en 
    : rulesData.value.value_ar;
});

const fetchRules = async () => {
  try {
    loading.value = true;
    error.value = null;
    const response = await axiosInstance.get('/app-terms');
    rulesData.value = response.data;
  } catch (err) {
    console.error('Error fetching park rules:', err);
    error.value = 'Failed to load park rules. Please try again.';
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchRules();
});
</script>

<style scoped>
.rules-section {
  position: relative;
  background: white;
  margin-top: 700px;
}

.wave-container {
  position: absolute;
  top: -200px;
  left: 0;
  width: 100%;
  height: 220px;
  overflow: hidden;
}

.wave-top {
  position: absolute;
  bottom: -20px;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: bottom;
  animation: waveFloat 5s ease-in-out infinite alternate;
}

.wave-path {
  fill: #ffffff;
  filter: drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.1));
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1268B1;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 1s linear infinite;
}

/* Error State */
.error-state {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.error-state i {
  font-size: 40px;
  color: #dc3545;
  margin-bottom: 20px;
}

.retry-btn {
  background: #1268B1;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 15px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s;
}

.retry-btn:hover {
  background: #0d4f8c;
}

/* Content Styling */
.rules-content {
  background: white;
  border-radius: 15px;
  padding: 40px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.content-wrapper {
  margin-bottom: 40px;
}

:deep(h1), :deep(h2), :deep(h3), :deep(strong) {
  color: #1268B1;
  margin: 1em 0;
  line-height: 1.4;
}

:deep(p) {
  margin: 1em 0;
  line-height: 1.6;
  color: #333;
}

:deep(ul), :deep(ol) {
  margin: 1em 0;
  padding-left: 1.5em;
}

:deep(li) {
  margin: 0.5em 0;
  line-height: 1.6;
  color: #333;
}

:deep(strong) {
  font-weight: 600;
}

/* Guidelines Section */
.guidelines-section {
  margin-top: 40px;
  padding-top: 40px;
  border-top: 2px solid #f0f0f0;
}

.guidelines-section h3 {
  text-align: center;
  color: #1268B1;
  margin-bottom: 30px;
}

.guidelines-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.guideline-card {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  transition: transform 0.3s ease;
}

.guideline-card:hover {
  transform: translateY(-5px);
}

.guideline-card i {
  font-size: 2rem;
  color: #1268B1;
  margin-bottom: 15px;
}

.guideline-card h4 {
  color: #1268B1;
  margin-bottom: 10px;
}

.guideline-card p {
  color: #666;
  font-size: 0.9rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes waveFloat {
  0% {
    transform: translateY(0) scaleY(1.02);
  }
  100% {
    transform: translateY(-10px) scaleY(1.05);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .container {
    padding: 20px;
  }

  .rules-content {
    padding: 20px;
  }

  .wave-container {
    top: -150px;
    height: 170px;
  }

  .guidelines-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .wave-container {
    top: -100px;
    height: 120px;
  }
}

/* RTL Support */
[dir="rtl"] .wave-top {
  transform: scaleX(-1);
}

[dir="rtl"] :deep(ul), [dir="rtl"] :deep(ol) {
  padding-right: 1.5em;
  padding-left: 0;
}

[dir="rtl"] .retry-btn i {
  transform: scaleX(-1);
}

[dir="rtl"] .guideline-card {
  text-align: center;
}
</style>